import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { FormatStandardDate } from "../../helpers/Date";
import { SharedBox, SharedBoxAttachment, SharedBoxAttachmentOption, SharedBoxMessage } from "../../types/SharedBox";
import './MessengerBox.scss';
import SharedBoxAPI from "../../api/endpoints/SharedBox";
import { GetAttachmentLink, ToHumanFileSize } from "../../helpers/SharedBox";
import AttachmentBox from "./AttachmentBox";
import { LocalFileAttachment } from "../../types/LocalFileAttachment";
import { PrepareFileToUpload } from "../../helpers/File";
import { GetFullHeight, GetPaddingHeight } from "../../helpers/Frame";

const MessageAttachments = ({
    hash,
    message,
    hasMessage,
}: {
    hash: string;
    message: SharedBoxMessage;
    hasMessage: boolean;
}) => {
    const [attachmentList, setAttachmentList] = useState<null|{
        normal: Array<SharedBoxAttachment>;
        signatureRequired: Array<SharedBoxAttachment>;
    }>(null);
    useEffect(() => {
        const messageFiles = (Array.isArray(message.attachments) ? message.attachments : []);
        const fileGrouped = messageFiles.reduce((memo: any, file: SharedBoxAttachment) => {
            const options = Array.isArray(file.options) ? file.options : [];
            file.signatureRequired = options.includes(SharedBoxAttachmentOption.SignatureRequired);
            const k = (
                file.signatureRequired ? 'signatureRequired' : 'normal'
            );
            memo[k].push(file);
            return memo;
        }, {
            normal: [],
            signatureRequired: []
        });
        setAttachmentList(fileGrouped);
    }, [message, message.hash]);
    const hasNormalAttachment = attachmentList && attachmentList.normal && attachmentList.normal.length > 0;
    const hasSignatureAttachment = attachmentList && attachmentList.signatureRequired && attachmentList.signatureRequired.length > 0;
    if (!hasNormalAttachment && !hasSignatureAttachment) return null;
    const onDownloadAttach = (attachment: SharedBoxAttachment) => {
        const endpoint = GetAttachmentLink(attachment);
        return (e: any) => {
            if (e && e.preventDefault) {
                e.preventDefault();
                e.stopPropagation();
            }
            window.open(endpoint, '_blank', 'noreferrer noopener');
        };
    }
    return (
        <div className={(hasMessage ? ' border-top mt-2 pt-1 ' : '')}>
            {attachmentList.normal.length > 0 ?
                    <ul className="m-0">
                        {attachmentList.normal.map((file: SharedBoxAttachment, index: number) => (
                            <li key={message.id + '_' + index}>
                                <a
                                    href="#"
                                    className="text-white"
                                    onClick={onDownloadAttach(file)}
                                >{file.filename}</a> ({ToHumanFileSize(file.fileSize || 0)})
                            </li>
                        ))}
                    </ul> : null
            }
            {attachmentList.signatureRequired.length > 0 ?
                <div className="bg-dark border rounded p-2 mt-2">
                    <div className="text-light fw-bold d-block">Documents requiring a signature:</div>
                    <ul className="m-0">
                        {attachmentList.signatureRequired.map((file: SharedBoxAttachment, index: number) => (
                            <li key={message.id + '_' + index}>
                                <a
                                    href="#"
                                    className="text-white"
                                    onClick={onDownloadAttach(file)}
                                >{file.filename}</a> ({ToHumanFileSize(file.fileSize || 0)})
                            </li>
                        ))}
                    </ul>
                </div>
                : null
            }
        </div>
    );
};

const MessageBubble = ({
    message,
    hash,
    isNew = false
}: {
    message: SharedBoxMessage;
    hash: string;
    isNew?: boolean;
}) => {
    const classNameBox = ['d-flex mb-3 flex-column'];
    const classNameContent = ['d-flex flex-column p-2', 'rounded', (isNew ? (
        message.isMe ?
            'animate__animated animate__backInRight' : 'animate__animated animate__backInLeft'
    ) : '')];
    const messageAlignClass = (
        message.isMe ?
            'justify-content-end' : 'justify-content-start'
    );
    classNameBox.push(messageAlignClass);
    classNameContent.push(
        message.isMe ?
            'bg-primary text-light' : 'bg-gray-message-100 text-light'
    );
    return (
        <div
            className={'MessengerBox_comp_MessageBubble' + classNameBox.join(' ')}
            data-message-hash={hash}
            // onMouseEnter={() => setHover(true)}
            // onMouseLeave={() => setHover(false)}
        >
            <div className={'d-flex flex-row ' + messageAlignClass}>
                <div className={'d-flex w-75 ' + messageAlignClass}>
                    <div className="d-block" style={{ maxWidth: '100%'}}>
                        <div className="text-muted d-block">
                            <span className="fw-semibold">{message.recipient?.aliasName}</span> - {FormatStandardDate(message.createdAt)}
                        </div>
                        <div className={classNameContent.join(' ')} style={{ maxWidth: '100%'}}>
                            {!!message.message ? <div>{message.message}</div> : null}
                            <MessageAttachments
                                hash={hash}
                                message={message}
                                hasMessage={!!message.message}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const UserInputForm = ({
    sharedBox,
    messages,
    refreshSharedBox,
    onPostMessage,
    isSendingMessage,
    attachments,
    setMessageAttachments,
}: {
    sharedBox: SharedBox|null;
    messages: Array<any>;
    refreshSharedBox: () => void;
    onPostMessage: (messageContent: string|null, attachments: Array<any>) => void;
    isSendingMessage: boolean;
    attachments: Array<LocalFileAttachment>;
    setMessageAttachments?: any;
}) => {
    const [gState, setGState] = useState({
        inputRef: useRef(null),
        messageBoxContent: useRef(null),
    });
    const canReplyText = (sharedBox?.canReplyText === true);
    const canReplyAttachment = (sharedBox?.canReplyAttachment === true);
    const canReply = canReplyText || canReplyAttachment;
    const handleAddMessage = async (e: any) => {
        if (!canReply) return;
        try {
            if (e) {
                e.preventDefault();
                e.target.blur();
            }
            const inputTarget = gState.inputRef.current as any;
            if (!inputTarget) return;
            onPostMessage(inputTarget.value, attachments);
            inputTarget.value = '';
        } catch (e: any) {
        }
    }
    return (
        canReply ? 
            <Form onSubmit={handleAddMessage} className="d-flex flex-column w-100">
                {sharedBox?.canReplyText ? 
                    <div className="mb-3">
                        <Form.Label className="fw-semibold">Message</Form.Label>
                        <Form.Control
                            ref={gState.inputRef}
                            disabled={isSendingMessage}
                            placeholder="Type your message here..."
                        />
                    </div>  : null
                }
                {sharedBox?.canReplyAttachment ?
                    <AttachmentBox
                        sharedBox={sharedBox}
                        attachments={attachments}
                        setMessageAttachments={setMessageAttachments}
                    /> : null
                }
                <div className="d-flex justify-content-end">
                    {isSendingMessage ?
                        <Spinner /> : <Button variant="primary" type="submit">Send message</Button>
                    }
                </div>
            </Form> :
            <div className="w-100">
                <Alert variant="info" className="mb-0 text-center px-2 py-2 rounded">
                    <span className="d-block mb-2">You cannot reply to this conversation.</span>
                    Please contact your accountant or Division Manager with any questions.
                </Alert>
            </div>
    );
}

export const MessengerBox = ({
    isMobileView = false,
    sharedBox,
    messages,
    refreshSharedBox,
}: {
    isMobileView?: boolean;
    sharedBox: SharedBox|null;
    messages: Array<any>;
    refreshSharedBox: () => void;
}) => {
    const [gState, setGState] = useState({
        messageBoxContent: useRef(null),
    });
    const [messageAttchments, setMessageAttachments] = useState<LocalFileAttachment[]>([]);
    const [isSendingMessage, setSendingMessage] = useState<boolean>(false);
    const handlePostMessage = async (messageContent: string|null, attachments: Array<any>) => {
        setSendingMessage(true);
        try {
            const content = messageContent;
            if ((!content || !content.trim()) && attachments.length === 0) throw new Error('Invalid content');
            const formData = new FormData();
            formData.append('message', content || '');
            for (const file of attachments) {
                PrepareFileToUpload(file, formData);
            }
            await SharedBoxAPI.postMessageWithAttachments(formData);
            setMessageAttachments([]);
            await refreshSharedBox();
        } catch (e: any) {
            console.error(e);
        }
        setSendingMessage(false);
    };
    const handleScrollBar = (position: any, instant: boolean = false) => {
        const viewBox = gState.messageBoxContent;
        if (viewBox.current) {
            const box: any = viewBox.current;
            if (box.scrollTo && instant != true) {
                box.scrollTo({
                    top: position,
                    left: 0,
                    behavior: 'smooth'
                });
            } else {
                box.scrollTop = position;
            }
        }
    };
    useEffect(() => {
        const viewBox = gState.messageBoxContent;
        if (viewBox.current) {
            const box: any = viewBox.current;
            handleScrollBar(box.scrollHeight);
        }
    }, [(sharedBox || {}).hash]);
    const handleResizeBoxContent = () => {
        const mainLayoutMenu = document.getElementById('app-main-layout-menu');
        const messengerHeader = document.getElementById('messenger-header');
        const messengerBodyBox = document.getElementById('messenger-body-box');
        const messageBoxInputs = document.getElementById('message-box-inputs');
        const conversationTitle = document.getElementById('conversation-title-header');
        const maxHeight = window.innerHeight - (
            GetFullHeight(mainLayoutMenu) +
            GetFullHeight(messengerHeader) +
            GetFullHeight(messageBoxInputs) +
            GetFullHeight(conversationTitle) +
            GetPaddingHeight(messengerBodyBox)
        );

        const messageBox: any = gState.messageBoxContent?.current;
        if (messageBox) {
            messageBox.style.height = maxHeight + 'px';
        }
    };
    useEffect(() => {
        handleResizeBoxContent();
        window.addEventListener('resize', handleResizeBoxContent);
        return () => {
            window.removeEventListener('resize', handleResizeBoxContent);
        };
    }, []);
    return (
        <div className="d-flex flex-column h-100 position-relative">
            <div className="d-flex flex-column flex-grow-1">
                <div
                    id="message-box-content"
                    ref={gState.messageBoxContent}
                    style={{
                        maxHeight: 470,
                        minHeight: 165,
                        overflow: 'auto'
                    }}>
                    {messages.map((message) => 
                        <MessageBubble
                            key={message.id}
                            hash={message.hash}
                            message={message}
                            isNew={message.isNewMessage === true}
                        />
                    )}
                </div>
            </div>
            <div id="message-box-inputs">
                <div className="d-flex pt-3 border-top">
                    {sharedBox ?
                            <UserInputForm
                                sharedBox={sharedBox}
                                messages={messages}
                                refreshSharedBox={refreshSharedBox}
                                onPostMessage={handlePostMessage}
                                isSendingMessage={isSendingMessage}
                                attachments={messageAttchments}
                                setMessageAttachments={setMessageAttachments}
                            /> : null
                    }
                </div>
                {isSendingMessage ?
                    <div
                        className="position-absolute d-flex h-100 w-100 bg-white justify-content-center align-items-center"
                    >
                        <div className="text-center">
                            <div className="d-block mb-3">
                                <Spinner />
                            </div>
                            Sending your message.<br/>
                            <span className="fw-semibold">Please do not close this window.</span>
                        </div>
                    </div> : null
                }
            </div>
        </div>
    )
};

export default MessengerBox;